/**
 * formSync.js — Sincroniza respuestas de formulario → meta_data WooCommerce customer
 *
 * La configuración de mapeos vive exclusivamente en la DB:
 *   /wp-json/datofy/v1/form-sync/configs
 *
 * Si no hay configuración para un formulario, el sync no corre — los campos
 * del tab Antecedentes quedan vacíos hasta que el admin los configure.
 *
 * Uso:
 *   import { syncFormResponseToCustomer } from 'services/formSync';
 *   await syncFormResponseToCustomer({ formSlug, category, responseData, customerId });
 */

import axios from 'axios';
import { getCredentials, getConfig, updateCustomer } from 'services/woo';

// ---------------------------------------------------------------------------
// Cache en memoria — evita llamadas repetidas dentro de la misma sesión
// ---------------------------------------------------------------------------
let _configCache = null;
let _cacheTime   = null;
const CACHE_TTL_MS = 5 * 60 * 1000;

const getWpApiUrl = () => {
  const creds = getCredentials();
  return getConfig(creds, 'WP_API_URL') || '';
};

// ---------------------------------------------------------------------------
// Cargar configs desde DB
// ---------------------------------------------------------------------------
export const loadFormSyncConfigs = async (forceRefresh = false) => {
  const now = Date.now();
  if (!forceRefresh && _configCache && _cacheTime && (now - _cacheTime < CACHE_TTL_MS)) {
    return _configCache;
  }

  const token = localStorage.getItem('token');
  const res   = await axios.get(`${getWpApiUrl()}/datofy/v1/form-sync/configs`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  _configCache = res.data?.data || {};
  _cacheTime   = now;
  return _configCache;
};

// ---------------------------------------------------------------------------
// CRUD de configs (usado por el panel Sistema > Sync Formularios)
// ---------------------------------------------------------------------------
export const saveFormSyncConfig = async (config) => {
  const token = localStorage.getItem('token');
  const res = await axios.post(`${getWpApiUrl()}/datofy/v1/form-sync/configs`, config, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  _configCache = null;
  return res.data;
};

export const updateFormSyncConfig = async (slug, config) => {
  const token = localStorage.getItem('token');
  const res = await axios.put(`${getWpApiUrl()}/datofy/v1/form-sync/configs/${slug}`, config, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  _configCache = null;
  return res.data;
};

export const deleteFormSyncConfig = async (slug) => {
  const token = localStorage.getItem('token');
  const res = await axios.delete(`${getWpApiUrl()}/datofy/v1/form-sync/configs/${slug}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  _configCache = null;
  return res.data;
};

// ---------------------------------------------------------------------------
// Obtener config para un formulario específico
// Prioridad: slug exacto → categoría (trigger auto)
// Retorna null si no hay configuración → el sync no corre
// ---------------------------------------------------------------------------
export const getFormSyncConfig = async (formSlug, formCategory) => {
  let configs;
  try {
    configs = await loadFormSyncConfigs();
  } catch {
    return null;
  }

  if (configs[formSlug]) return configs[formSlug];

  const byCategory = Object.values(configs).find(
    cfg => cfg.category === formCategory && cfg.trigger !== 'manual'
  );
  return byCategory || null;
};

// ---------------------------------------------------------------------------
// Construir responseData { questionId: valor } desde questions[] + formData
// formField en los configs de Sync Formularios es el ID numérico de la pregunta.
// ---------------------------------------------------------------------------
export const buildResponseDataFromQuestions = (questions, formData) => {
  const result = {};
  if (!questions || !formData) return result;

  questions.forEach(q => {
    const value = formData[q.id];
    if (value === undefined || value === null || value === '' || value instanceof FileList) return;
    result[String(q.id)] = String(value).trim();
  });

  return result;
};

// ---------------------------------------------------------------------------
// Construir payload WooCommerce desde la config y los datos normalizados
// ---------------------------------------------------------------------------
// skipKeys: Set de "target:key" a excluir del payload — usado por el auto-sync
// (modo onlyMissing) para no pisar campos que el customer ya tiene, sin por
// eso saltarse la sección completa como antes.
const buildCustomerPayload = (syncConfig, responseData, skipKeys) => {
  const payload = { meta_data: [] };
  const billing = {};
  const shouldSkip = (target, key) => skipKeys && skipKeys.has(`${target}:${key}`);

  for (const mapping of syncConfig.fieldMap) {
    const value = responseData[mapping.formField];
    if (!value) continue;

    if (!shouldSkip(mapping.target, mapping.key)) {
      if (mapping.target === 'root')         payload[mapping.key] = value;
      else if (mapping.target === 'billing') billing[mapping.key] = value;
      else if (mapping.target === 'meta')    payload.meta_data.push({ key: mapping.key, value });
    }

    for (const also of mapping.syncAlso || []) {
      if (shouldSkip(also.target, also.key)) continue;
      if (also.target === 'root')         payload[also.key] = value;
      else if (also.target === 'billing') billing[also.key] = value;
      else if (also.target === 'meta')    payload.meta_data.push({ key: also.key, value });
    }
  }

  if (Object.keys(billing).length) payload.billing = billing;
  if (!payload.meta_data.length)   delete payload.meta_data;

  return payload;
};

// ---------------------------------------------------------------------------
// Función principal — aplica TODOS los configs que coincidan con el formulario
// Un formulario puede tener múltiples configs (distintos slugs, misma categoría)
// → cada uno produce un card en Antecedentes y todos se sincronizan juntos.
// ---------------------------------------------------------------------------
export const syncFormResponseToCustomer = async ({ formSlug, formCategory, responseData, customerId, skipKeys, extraMeta }) => {
  try {
    let allConfigs;
    try { allConfigs = await loadFormSyncConfigs(); } catch { allConfigs = {}; }

    // Todos los configs que aplican: coincidencia exacta de slug O misma categoría
    const matchingConfigs = Object.values(allConfigs).filter(cfg =>
      cfg.slug === formSlug || cfg.category === formCategory
    );

    if (!matchingConfigs.length) {
      return { synced: false, fields: [], reason: 'Sin configuración de sync para este formulario' };
    }

    // Merge de payloads de todos los configs que aplican
    const merged   = { meta_data: [] };
    const billing  = {};
    matchingConfigs.forEach(cfg => {
      const p = buildCustomerPayload(cfg, responseData, skipKeys);
      Object.entries(p).forEach(([k, v]) => {
        if (k === 'meta_data') merged.meta_data.push(...v);
        else if (k === 'billing') Object.assign(billing, v);
        else merged[k] = v;
      });
    });
    if (Object.keys(billing).length) merged.billing = billing;
    // extraMeta: metadatos de control del sync (marcador de ficha, claves editadas a mano)
    if (extraMeta?.length) merged.meta_data.push(...extraMeta);
    if (!merged.meta_data.length)    delete merged.meta_data;

    const payload      = merged;
    const fieldsToSync = [
      ...Object.keys(payload).filter(k => k !== 'meta_data' && k !== 'billing'),
      ...(payload.billing   ? Object.keys(payload.billing).map(k => `billing.${k}`) : []),
      ...(payload.meta_data ? payload.meta_data.map(m => m.key) : []),
    ];

    if (!fieldsToSync.length) {
      return { synced: false, fields: [], reason: 'Sin campos para sincronizar' };
    }

    const result = await updateCustomer(customerId, payload);
    return {
      synced: result.success,
      fields: fieldsToSync,
      error:  result.success ? undefined : result.message,
    };
  } catch (error) {
    return { synced: false, fields: [], error: error.message };
  }
};
